import type { CSSProperties } from "react";
import { cx, MarkerIcon } from "../shared/markers";
import { UiIcon } from "./Icon";
import { strings } from "./i18n";
import type { PartnerMapContent, Place, SisterRestaurant } from "./types";
import styles from "./PartnerMap.module.scss";

/** The sister restaurant as the content gives it: its entry in `venues` and its own words, or null for a partner that has none. */
export function sisterOf(content: PartnerMapContent): { sister: SisterRestaurant; place: Place } | null {
	const sister = content.contact?.sister;
	const place = sister && content.venues.find((venue) => venue.slug === sister.venue);
	return sister && place ? { sister, place } : null;
}

/**
 * The custom properties the sister restaurant's colours set where its card, buttons and label are (`PartnerMap.module.scss` reads them): its `color`, and for a dark sister
 * (one with an `ink` or a round `marker`) its ink, with the card's overlay and logo plate in that colour itself, not a shade of it as a light one has them.
 */
export const sisterVars = ({ color, ink, marker }: SisterRestaurant): CSSProperties =>
	({
		"--sister": color,
		...(ink || marker ? { "--sister-deep": color, "--sister-plate": color } : {}),
		...(ink ? { "--sister-ink": ink } : {}),
	}) as CSSProperties;

/** What its marker on the map wears besides `color`: the name plate's ink, the rim of a round marker, and whether it is round. Nothing for a sister that is only a colour. */
export const sisterMarker = ({ ink, ring, marker }: SisterRestaurant): { ink?: string; ring?: string; round?: true } => ({
	...(ink ? { ink } : {}),
	...(ring ? { ring } : {}),
	...(marker === "round" ? { round: true as const } : {}),
});

interface SisterCardProps {
	content: PartnerMapContent;
	onOpen: (slug: string) => void;
	onContact: (channel: string) => void;
}

/** The sister restaurant's card in the Highlights and contact tabs: its photo, wordmark, how far it is and what it is called, then "Book at ..." and "See on the map". */
const SisterCard = ({ content, onOpen, onContact }: SisterCardProps) => {
	const found = sisterOf(content);
	if (!found) return null;
	const { sister, place } = found;
	const t = strings(content.lang);
	return (
		<div className={styles.sister} style={sisterVars(sister)}>
			<button type="button" className={styles.sisterTop} aria-label={`${place.name} ${t.onMap.toLowerCase()}`} onClick={() => onOpen(place.slug)}>
				<img className={styles.sisterPh} src={place.photo} alt="" loading="lazy" />
				<span className={styles.sisterOv} />
				<span className={styles.sisterLogo}>
					<img src={sister.logo} alt={place.name} />
				</span>
				{place.reach && (
					<span className={styles.bike}>
						<UiIcon name="directions" />
						{place.reach}
					</span>
				)}
				<span className={styles.sisterTx}>
					<small>{sister.tagline}</small>
					<b>{place.name}</b>
				</span>
			</button>
			<div className={styles.sisterBot}>
				<a
					id="partnermap-sister-book"
					className={cx(styles.btn, styles.sisterBtn)}
					href={sister.book}
					target="_blank"
					rel="noopener"
					onClick={() => onContact("sister_book")}
				>
					<MarkerIcon name="p_restaurant" size={15} />
					{t.bookAt(sister.shortName)}
				</a>
				<button type="button" className={styles.btn} onClick={() => onOpen(place.slug)}>
					<UiIcon name="directions" />
					{t.seeOnMap}
				</button>
			</div>
		</div>
	);
};

export default SisterCard;
